/**
 * Shared constants and helpers for the Tasks module.
 * Extracted from Tasks.js, TodaysTask.js, and FindMyTask.js to avoid duplication.
 */

/** Parse all_roles from localStorage (JSON array or legacy comma-separated string). */
export const parseAllRolesFromStorage = () => {
  const raw = localStorage.getItem("all_roles");
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((role) => String(role).trim()).filter(Boolean);
    }
  } catch (err) {
    // fall through to comma-separated parsing
  }

  return raw
    .split(",")
    .map((role) => role.trim().replace(/^["']+|["']+$/g, ""))
    .filter(Boolean);
};

/** Parse ticket_role from localStorage (comma-separated or JSON array). */
export const parseTicketRolesFromStorage = () => {
  const raw = localStorage.getItem("ticket_role");
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((role) => String(role).trim()).filter(Boolean);
    }
  } catch (err) {
    // fall through to comma-separated parsing
  }

  return raw
    .split(",")
    .map((role) => role.trim().replace(/^["']+|["']+$/g, ""))
    .filter(Boolean);
};

/** Roles used for Tasks page tab visibility (all_roles + ticket_role + active user_role). */
export const getTasksPageRoles = () => {
  const roles = new Set([
    ...parseAllRolesFromStorage(),
    ...parseTicketRolesFromStorage(),
  ]);
  const activeRole = localStorage.getItem("user_role");
  if (activeRole) {
    roles.add(activeRole.trim());
  }
  return [...roles];
};

// Maps task names (from API) to their corresponding tab keys
export const TASK_NAME_MAP = {
  "Assign Coordinator": "visible1",
  "Work Order Price Validation": "visible81",
  "Verify Final Delivery Price": "visible8",
  "Additional Work Order": "visible9",
  "Work Order Advance payment": "visible91",
  "Approve Prepayment": "visible92",
  Miscellaneous: "visible93",
  "New Ticket": "visible2",
  "Allocate Partner": "visible3",
  "Schedule Ticket": "visible4",
  "Check Schedule": "visible5",
  active_visit: "visible51",
  no_work_order_issued: "visible52",
  "Upload Documents": "visible6",
  "Close Ticket": "visible7",
  "Rejected WorkOrder": "visible71",
  hard_copy: "visible10",
  "Hard Copy Rejected": "visible13",
  "Overdue Visit": "visible51",
  "Active Visit": "visible51",
  "Upcoming Visit": "visible51",
  "Work order task": "visible52",
  "Soft Copy Rejected": "visible99",
  "Verify Parner Invoice Delete": "visible14",
  "Project expense task": "visible17",
  "Ticket expense task": "visible16",
  "Retain CT WO": "visible15",
  "Failed Visits": "visible51",
  "Audit Visits": "visible100",
  "Not Invoiced  Wo": "visible101",
  "New Actions": "visible102",
  "PM Approval for visit Action Addition": "visible102",
  "IR deletion": "visible103",
  "Pre-Approved Actions": "visible104",
  "Stalled Tasks PM": "visible106",
  "Stalled Tasks": "visible105",
  "Stalled ticket": "visible105",
};

// PM-only tasks that should be hidden from coordinators
export const PM_ONLY_TASKS = [
  "Assign Coordinator",
  "Work Order Price Validation",
  "Verify Final Delivery Price",
  "Additional Work Order",
  "Work Order Advance payment",
  "Approve Prepayment",
  "Verify Parner Invoice Delete",
  "Project expense task",
  "Ticket expense task",
  "Retain CT WO",
  "New Actions",
  "PM Approval for visit Action Addition",
  "Miscellaneous",
];

// Check if user has PM role
export const isProjectManager = () => {
  const userRoles = getTasksPageRoles();
  return userRoles.includes("project_manager") || userRoles.includes("admin");
};

// Check if task is allowed for current user role
export const isTaskAllowedForUser = (taskName) => {
  if (isProjectManager()) {
    return true;
  }
  return !PM_ONLY_TASKS.includes(taskName);
};

/**
 * Returns inline style for task count badge based on count value.
 * - count > 0  → red background (pending tasks)
 * - count === 0 or unavailable → grey background (no pending / unknown)
 */
export const getTaskBadgeStyle = (count) => {
  const numCount = Number(count);
  if (!count || isNaN(numCount) || numCount === 0) {
    return { backgroundColor: "#bfbfbf", color: "#fff" }; // grey
  }
  return { backgroundColor: "#f44336", color: "#fff" }; // red — has pending
};

/** Open PM tasks that block visit end / action completion until approved. */
export const VISIT_PM_APPROVAL_TASK_NAMES = [
  "PM Approval for visit Action Addition",
  "Work Order Price Validation",
];

export const taskVisitApprovalResponsePending = (task) =>
  task &&
  task.status === "open" &&
  (task.response === undefined ||
    task.response === null ||
    String(task.response).trim() === "");

export const findOpenVisitPmApprovalTask = (ticketRow, visitId) => {
  const tasks = ticketRow?.action_task_count;
  if (
    !tasks?.length ||
    visitId === undefined ||
    visitId === null ||
    visitId === ""
  ) {
    return null;
  }
  const visitIdStr = String(visitId);
  return (
    tasks.find(
      (task) =>
        String(task.visit_id) === visitIdStr &&
        taskVisitApprovalResponsePending(task) &&
        VISIT_PM_APPROVAL_TASK_NAMES.includes(task.name)
    ) || null
  );
};

export const getPmApprovalTaskBlockMessage = (taskName) => {
  if (taskName === "Work Order Price Validation") {
    return "Work Order Price Validation is pending with the project manager. End visit cannot be completed until the updated work order price is approved.";
  }
  if (taskName === "PM Approval for visit Action Addition") {
    return "PM approval for new visit actions is still pending. End visit cannot be completed until the project manager finishes their review.";
  }
  return `A pending task (“${taskName}”) must be completed before you can end this visit.`;
};

export const VISIT_ACTIONS_MISSING_MESSAGE =
  "We could not load the scheduled actions for this visit. This sometimes happens on older tickets where visit actions were not saved correctly. Please refresh the ticket details or contact support if the issue continues.";

export const getVisitActionsMissingMessage = (visitNumber) => {
  if (visitNumber) {
    return `We could not load scheduled actions for visit #${visitNumber}. This sometimes happens when a revisit was created without action wiring. Please refresh the ticket details or contact support.`;
  }
  return VISIT_ACTIONS_MISSING_MESSAGE;
};

/** Project SOW or ticket-level counts — used to decide if visit actions are expected. */
export const ticketExpectsVisitActions = (
  sowActions = [],
  ticketDetails
) => {
  const details = ticketDetails || {};
  return !!(
    (sowActions && sowActions.length > 0) ||
    (details.action_with_count && details.action_with_count.length > 0) ||
    (details.revisit_action_count && details.revisit_action_count.length > 0)
  );
};

/** Resolve overlay mode for action-based visit end flows. */
export const getVisitActionsOverlayState = ({
  visitList = [],
  ticketDetails,
  sowActions = [],
  allocatedCount = 0,
  dataReady = true,
}) => {
  const details = ticketDetails || {};

  if (!dataReady || !visitList.length || !ticketDetails) {
    const expectsActions = ticketExpectsVisitActions(sowActions, details);
    return expectsActions
      ? { mode: "loading", message: "" }
      : { mode: "hidden", message: "" };
  }

  const expectsActions = ticketExpectsVisitActions(sowActions, details);

  const sortedVisits = [...visitList].sort(
    (a, b) => b.visit_number - a.visit_number
  );
  const currentVisit = sortedVisits[0];
  const openPmTask = findOpenVisitPmApprovalTask(
    ticketDetails,
    currentVisit?.id
  );

  if (openPmTask) {
    return {
      mode: "blocked",
      message: getPmApprovalTaskBlockMessage(openPmTask.name),
    };
  }

  if (!expectsActions) {
    return { mode: "hidden", message: "" };
  }

  if (allocatedCount === 0) {
    return {
      mode: "blocked",
      message: getVisitActionsMissingMessage(currentVisit?.visit_number),
    };
  }

  return { mode: "hidden", message: "" };
};
